import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Compass, Flame, Heart } from "lucide-react";
import { STATS } from "@/data/content";

const HERO_BG =
  "https://images.unsplash.com/photo-1612892483236-52d32a0e0ac1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxzb21iZXIlMjBkZXNlcnQlMjBtb3VudGFpbiUyMGxhbmRzY2FwZSUyMHN1bnNldHxlbnwwfHx8fDE3ODExNDIzODR8MA&ixlib=rb-4.1.0&q=85";

const Pillar = ({ icon: Icon, title, body, to, cta, testid }) => (
  <div
    className="border border-white/10 p-8 lg:p-10 hover:border-[#D4AF37]/40 transition-colors duration-500 bg-[#111215]/50 flex flex-col"
    data-testid={`pillar-${testid}`}
  >
    <Icon className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.25} />
    <h3 className="font-serif-display text-2xl text-[#F2EBE5] mb-3">{title}</h3>
    <p className="text-sm text-[#A19D98] leading-relaxed flex-1">{body}</p>
    <Link
      to={to}
      className="overline mt-8 inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C07B] transition-colors"
      data-testid={`pillar-cta-${testid}`}
    >
      {cta} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
    </Link>
  </div>
);

export const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/60 to-[#0A0A0C]" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32 pt-32 w-full">
          <div className="overline fade-up text-[#D4AF37]">August 3, 2014 — Sinjar, Iraq</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl text-[#F2EBE5] mt-6 max-w-4xl leading-[1.05] fade-up-delay-1">
            For the Yazidis of Sinjar,<br />
            <span className="text-[#D4AF37] italic">we remember.</span>
          </h1>
          <p className="text-base lg:text-lg text-[#A19D98] mt-8 max-w-2xl leading-relaxed fade-up-delay-2">
            On August 3, 2014, ISIS attacked the Yazidi homeland in Sinjar, Iraq.
            Within days, thousands were killed, thousands more were abducted, and
            hundreds of thousands were forced to flee for the mountain. This is a
            memorial — and an interactive learning experience — built so the world
            does not forget.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 fade-up-delay-3">
            <Link
              to="/path"
              data-testid="hero-start-path"
              className="px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 inline-flex items-center gap-2"
            >
              Walk the Path of Survival <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/timeline"
              data-testid="hero-timeline"
              className="px-6 py-3 border border-white/20 text-[#F2EBE5] text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500"
            >
              See the Timeline
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO STATS BANNER */}
      <section className="border-y border-white/10 bg-[#0A0A0C]" data-testid="home-stats">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10">
          {STATS.map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="font-serif-display text-2xl lg:text-3xl text-[#D4AF37]">{s.value}</div>
              <div className="overline mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="home-pillars">
        <div className="overline">How to engage</div>
        <h2 className="font-serif-display text-3xl lg:text-4xl text-[#F2EBE5] mt-3 mb-12 max-w-2xl">
          Four ways to learn, remember, and act.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          <Pillar
            icon={Compass}
            title="The Path of Survival"
            body="A decision-based narrative — walk in the footsteps of a Yazidi family on August 3, 2014. Your choices reflect the impossible decisions that families really faced."
            to="/path"
            cta="Begin the path"
            testid="path"
          />
          <Pillar
            icon={BookOpen}
            title="Survivor Stories"
            body="Read public testimonies from survivors, including Nobel Peace Prize laureate Nadia Murad. Their voices are the truest record."
            to="/stories"
            cta="Read voices"
            testid="stories"
          />
          <Pillar
            icon={Flame}
            title="A Place of Remembrance"
            body="Light a candle. Leave a message of remembrance for the lost, the missing, and the survivors of Sinjar."
            to="/memorial"
            cta="Light a candle"
            testid="memorial"
          />
          <Pillar
            icon={Heart}
            title="Take Action"
            body="Support survivor-led organizations rebuilding lives and seeking justice. Justice for the Yazidis is unfinished — and so is the work."
            to="/act"
            cta="See organizations"
            testid="act"
          />
        </div>
      </section>

      {/* CONTEXTUAL QUOTE */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="home-quote">
        <div className="overline text-[#D4AF37]">From a survivor</div>
        <blockquote className="font-serif-display text-2xl lg:text-3xl italic text-[#F2EBE5] mt-6 leading-snug">
          “We do not want pity. We want to be remembered, and we want justice.
          Those are not the same thing — but we need both.”
        </blockquote>
        <div className="text-xs text-[#6E6B67] mt-6">— Public testimony, Yazidi survivor, 2018</div>
      </section>
    </div>
  );
};

export default Home;
